import React, { useEffect, useMemo, useState } from 'react';

import isEqual from 'lodash/isEqual';
import { SubmitHandler, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Save } from '@mui/icons-material';
import { Box, Button, Grid, MenuItem, Stack, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  getGridNumericOperators,
  getGridStringOperators,
} from '@mui/x-data-grid';
import { DateRangePicker, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';

import LereService from '~/services/LereService';
import { formatDateDayShortDayName, formatDateISO, formatDateSlashes } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';

import GridPagination from '../Common/Elements/GridPagination';
import { Checkbox, Select } from '../Common/Forms/fields';
import Input from '../Common/Forms/fields/Input';
import LoadingButton from '../Common/LoadingButton';
import { useConfirmDialog } from '../Common/Providers/ConfirmDialogProvider';

import CharterTypeFiltersDropdown from './CharterTypeFiltersDropdown';
import NameAndIdFormatter from './formatters/NameAndIdFormatter';
import SecondColumnStatisticsFormatter from './formatters/SecondColumnStatisticsFormatter';
import StatisticsFormatter from './formatters/StatisticsFormatter';
import {
  CharterFilteringOptions,
  ConfidenceScoreTier,
  LerePaginatedResponse,
  LereTabComponentProps,
  SuggestionType,
  SurchargeSuggestionBQ,
  SurchargeSuggestionFilters,
} from './types';

const ACCEPTED = 'accepted';
const REJECTED = 'rejected';
const equalsOnlyFilter = getGridStringOperators().filter((operator) => operator.value === 'equals');
const containsOnlyFilter = getGridStringOperators().filter((operator) => operator.value === 'contains');
const gteOnlyFilter = getGridNumericOperators().filter((operator) => operator.value === '>=');
const numericEqualsOnlyFilter = getGridNumericOperators().filter((operator) => operator.value === '=');

type FormInput = {
  offerId: string;
  propertyName: string;
  roomTypeName: string;
  aggregate: boolean;
  noRMSurchargeApplied: boolean;
  hasRecentSales: boolean;
  hasFourOrMoreRooms: boolean;
  suggestionType: SuggestionType;
  scoreTier: ConfidenceScoreTier;
  onlyCharters: CharterFilteringOptions;
};

const getRowId = (row: SurchargeSuggestionBQ): string =>
  JSON.stringify({
    property_id: row.property_id,
    room_type_id: row.room_type_id,
    room_rate_id: row.room_rate_id,
    start_date: row.suggested_start_date,
    end_date: row.suggested_end_date,
  });

export default function RMSurchargeSuggestions({ onLereError }: LereTabComponentProps) {
  const showConfirmDialog = useConfirmDialog();
  const { control, handleSubmit, reset } = useForm<FormInput>({
    defaultValues: {
      offerId: '',
      propertyName: '',
      roomTypeName: '',
      aggregate: true,
      noRMSurchargeApplied: false,
      hasRecentSales: false,
      hasFourOrMoreRooms: false,
      suggestionType: 'increase',
      scoreTier: ConfidenceScoreTier.ANY,
      onlyCharters: CharterFilteringOptions.ONLY_NON_CHARTERS,
    },
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [surchargeSuggestionData, setSurchargeSuggestionData] = useState<LerePaginatedResponse<SurchargeSuggestionBQ>>({
    result: [],
    total: 0,
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 15,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filters, setFilters] = useState<undefined | SurchargeSuggestionFilters>({
    aggregate: true,
    onlyCharters: CharterFilteringOptions.ONLY_NON_CHARTERS,
  });
  const [actionedRows, setActionedRows] = useState<Array<string>>([]);
  const visbleColumns: Array<GridColDef<SurchargeSuggestionBQ>> = [
    {
      field: 'offer_id',
      headerName: 'Offer Id',
      display: 'flex',
      flex: 1,
      filterOperators: equalsOnlyFilter,
      renderCell: ({ row }) => <NameAndIdFormatter id={row.offer_id} to={`/offers/${row.offer_id}`} />,
    },
    {
      field: 'property_name',
      headerName: 'Property Name',
      display: 'flex',
      flex: 1,
      filterable: true,
      filterOperators: containsOnlyFilter,
      renderCell: ({ row }) => (
        <NameAndIdFormatter
          name={row.property_name}
          id={row.property_id}
          to={`/vendors/${row.vendor_id}/properties/${row.property_id}`}
        />
      ),
    },
    {
      field: 'room_type_name',
      headerName: 'Room Type Name',
      display: 'flex',
      flex: 1,
      filterable: true,
      filterOperators: containsOnlyFilter,
      renderCell: ({ row }) => (
        <NameAndIdFormatter
          name={row.room_type_name}
          id={row.room_type_id}
          to={`/vendors/${row.vendor_id}/properties/${row.property_id}/room-types/${row.room_type_id}/room-rates/${row.room_rate_id}`}
        />
      ),
    },
    {
      field: 'suggested_start_date',
      headerName: 'Start Date - End Date',
      display: 'flex',
      width: 200,
      editable: true,
      filterable: false,
      renderCell: ({ row }) => {
        const startDate = new Date(row.actioned_start_date);
        const endDate = new Date(row.actioned_end_date);
        return `${formatDateSlashes(startDate)} (${formatDateDayShortDayName(startDate)}) - ${formatDateSlashes(
          endDate,
        )} (${formatDateDayShortDayName(endDate)})`;
      },
      renderEditCell: ({ id, api, row }) => (
        <DateRangePicker
          value={[dayjs(row.actioned_start_date), dayjs(row.actioned_end_date)]}
          onChange={([startDate, endDate]) => {
            if (startDate && endDate) {
              api.setEditCellValue({
                id,
                field: 'suggested_start_date',
                value: {
                  actioned_start_date: formatDateISO(startDate.toDate()),
                  actioned_end_date: formatDateISO(endDate.toDate()),
                },
              });
            }
          }}
          calendars={1}
          slots={{ field: SingleInputDateRangeField }}
        />
      ),
    },
    {
      field: 'statistics',
      headerName: 'Statistics',
      display: 'flex',
      flex: 2.1,
      filterable: false,
      sortable: false,
      renderCell: StatisticsFormatter,
    },
    {
      field: 'statistics_continued',
      headerName: '',
      display: 'flex',
      flex: 2.1,
      filterable: false,
      sortable: false,
      renderCell: SecondColumnStatisticsFormatter,
    },
    {
      field: 'actioned_surcharge',
      headerName: 'Suggested Surcharge',
      width: 100,
      editable: true,
      filterable: false,
      sortable: false,
      type: 'number',
      valueFormatter: (value: number) => currencyFormatter('AUD', value, 2),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      type: 'singleSelect',
      valueOptions: [ACCEPTED, REJECTED],
      editable: true,
      filterable: false,
      sortable: false,
      valueFormatter: (value: string) => value?.toUpperCase(),
    },
    {
      field: 'note',
      headerName: 'Note',
      flex: 0.7,
      display: 'flex',
      type: 'string',
      filterable: false,
      sortable: false,
      editable: true,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      filterable: false,
      sortable: false,
      width: 80,
      renderCell: ({ row }) => {
        return (
          <LoadingButton
            DefaultIcon={Save}
            onSave={() => handleCreateSurcharge(row)}
            disabled={
              !(row.status === ACCEPTED || row.status === REJECTED) ||
              actionedRows.includes(getRowId(row)) ||
              // disable this suggestion type for now
              row.suggestion_type === 'remove'
            }
          />
        );
      },
    },
  ];
  const invisibleColumns: Array<GridColDef<SurchargeSuggestionBQ>> = [
    {
      field: 'internal_surcharge_total',
      headerName: 'Internal Surcharge',
      type: 'number',
      filterOperators: numericEqualsOnlyFilter,
    },
    { field: 'sales_last_3_days', headerName: 'Sales Last 3 Days', type: 'number', filterOperators: gteOnlyFilter },
    { field: 'booked', headerName: 'Booked', type: 'number', filterable: false },
    { field: 'allocated', headerName: 'Allocated', type: 'number', filterOperators: gteOnlyFilter },
    { field: 'vendor_surcharge_total', headerName: 'Vendor Surcharge', type: 'number', filterable: false },
  ];

  async function handleCreateSurcharge(row: SurchargeSuggestionBQ): Promise<void> {
    if (row.status === ACCEPTED) {
      const adjustedAfterPrice = row.suggested_after_price + (row.actioned_surcharge - row.suggested_surcharge);
      let warningMsg = '';
      if (adjustedAfterPrice > row.next_cheapest_room_price - 10) {
        // Calculate how much we need to reduce the surcharge to stay within pricing boundaries

        warningMsg = `(WARNING: This would put this room within $10 of the next cheapest room. We will surcharge the next room as well as this one to maintain a $10 buffer.)`;
      }

      const userConfirmed = await showConfirmDialog({
        title: 'Confirm Surcharge Update',
        description: `This change will modify the surcharge and overwrite any existing dates within the specified range. Do you wish to continue? ${warningMsg}`,
      });

      if (!userConfirmed) {
        throw new Error('Surcharge update was cancelled by the user.');
      }
    }

    await LereService.createSurchargeSuggestion(row);
    setActionedRows((prev) => [...prev, getRowId(row)]);
  }

  const onSubmit: SubmitHandler<FormInput> = (data) => {
    const newFilters = {
      aggregate: data.aggregate,
      offerIdEquals: data.offerId,
      propertyNameLike: data.propertyName,
      roomTypeNameLike: data.roomTypeName,
      internalSurchargeEquals: data.noRMSurchargeApplied ? 0 : undefined,
      salesGte: data.hasRecentSales ? 1 : undefined,
      allocatedGte: data.hasFourOrMoreRooms ? 4 : undefined,
      suggestionType: data.suggestionType,
      confidence: data.scoreTier !== ConfidenceScoreTier.ANY ? data.scoreTier : undefined,
      onlyCharters: data.onlyCharters,
    };

    if (isEqual(newFilters, filters)) {
      return;
    }
    setFilters(newFilters);
  };

  useEffect(() => {
    setIsLoading(true);
    setActionedRows([]);
    LereService.getLatestSurchargeSuggestions(paginationModel.page + 1, paginationModel.pageSize, {
      ...filters,
      sortBy: sortModel[0]?.field,
      ascending: sortModel[0]?.sort ? sortModel[0]?.sort === 'asc' : undefined,
    })
      .then((data) =>
        setSurchargeSuggestionData({
          ...data,
          result: data.result.map((d: SurchargeSuggestionBQ) => ({
            ...d,
            actioned_surcharge: d.suggested_surcharge,
            actioned_start_date: d.suggested_start_date,
            actioned_end_date: d.suggested_end_date,
            next_cheapest_room_price: d.next_cheapest_room_price ?? Infinity,
            status: 'pending',
            note: undefined,
          })),
        }),
      )
      .catch(onLereError)
      .finally(() => setIsLoading(false));
  }, [paginationModel, setSurchargeSuggestionData, onLereError, filters, sortModel]);
  const filteredResult = useMemo(() => {
    if (!surchargeSuggestionData?.result[0]?.suggested_start_date) {
      return []; // TODO: temporarily condition to return empty list while waiting for backend to deploy, remove later
    }
    return (surchargeSuggestionData?.result || []).filter((row) => row && getRowId(row));
  }, [surchargeSuggestionData]);
  return (
    <Box>
      <Typography variant="h2" fontSize="2em" fontWeight="medium">
        Search Surcharge Suggestions
      </Typography>
      <Stack direction="column" my={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Input disabled={isLoading} fullWidth control={control} muiLabel="Offer Id" name="offerId" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input disabled={isLoading} fullWidth control={control} muiLabel="Property Name" name="propertyName" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input disabled={isLoading} fullWidth control={control} muiLabel="Room Type Name" name="roomTypeName" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select disabled={isLoading} control={control} muiLabel="Surcharge Suggestion Type" name="suggestionType">
              <MenuItem key="increase" value="increase">
                INCREASE
              </MenuItem>
              <MenuItem key="remove" value="remove">
                REMOVE
              </MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select disabled={isLoading} control={control} muiLabel="Confidence Score" name="scoreTier">
              {Object.entries(ConfidenceScoreTier).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <CharterTypeFiltersDropdown control={control} name="onlyCharters" disabled={isLoading} />
          </Grid>
        </Grid>
        <Stack direction="row" alignItems="center">
          <Checkbox disabled={isLoading} control={control} label="Aggregate" name="aggregate" />
          <Checkbox
            disabled={isLoading}
            control={control}
            label="No RM Surcharge applied"
            name="noRMSurchargeApplied"
          />
          <Checkbox disabled={isLoading} control={control} label="Has sales in last 3 days" name="hasRecentSales" />
          <Checkbox disabled={isLoading} control={control} label="Has 4+ Rooms allocated" name="hasFourOrMoreRooms" />
        </Stack>
        <Stack direction="row" justifyContent="end" spacing={2}>
          <Button variant="text" disabled={isLoading} onClick={() => reset()}>
            Clear all filters
          </Button>
          <Button variant="contained" type="submit" disabled={isLoading} onClick={handleSubmit(onSubmit)}>
            Search
          </Button>
        </Stack>
      </Stack>
      <DataGrid
        autoHeight
        columns={[...visbleColumns, ...invisibleColumns]}
        rows={filteredResult}
        rowCount={surchargeSuggestionData.total}
        loading={isLoading}
        pagination
        paginationMode="server"
        pageSizeOptions={[5, 10, 15]}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        getRowHeight={() => 'auto'}
        getRowId={getRowId}
        slots={{
          pagination: GridPagination,
        }}
        columnVisibilityModel={invisibleColumns.reduce((acc, col) => ({ ...acc, [col.field]: false }), {})}
        disableRowSelectionOnClick
        disableColumnMenu
        sortingMode="server"
        onSortModelChange={setSortModel}
        processRowUpdate={(newRow, oldRow) => {
          // Handle date range updates
          if (newRow.suggested_start_date && typeof newRow.suggested_start_date === 'object') {
            const dateUpdate = newRow.suggested_start_date as {
              actioned_start_date: string;
              actioned_end_date: string;
            };
            return {
              ...newRow,
              actioned_start_date: dateUpdate.actioned_start_date,
              actioned_end_date: dateUpdate.actioned_end_date,
              suggested_start_date: oldRow.suggested_start_date, // Reset the field back to original
            };
          }
          return newRow;
        }}
      />
    </Box>
  );
}
